import { Skeleton } from 'antd'
import {
  AssignedProperty,
  AssignedPropertyValue,
  AssigneeType,
  DocumentPropertyValue,
  PersonPropertyValue,
  Property,
  PropertyValue,
  VirtualAssignedProperty,
} from 'api/clients/PropertyClient'
import { PropertyService } from 'api/services/PropertyService'
import cx from 'classnames'
import { PropertyActions } from 'common/PropertyActions/PropertyActions'
import { FolderPropertyListItem } from 'common/PropertyListItem/PropertyListItem'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useProperties } from 'hooks/useProperties'
import { useProperties as usePropertiesQuery } from 'hooks/queries/companies/properties/useProperties'
import isEqual from 'lodash/isEqual'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Permission, RootState, UserRole } from 'types'
import Styles from './PropertiesList.module.scss'
import { usePropertiesList } from './usePropertiesList'
import { useRootStore } from 'stores/RootStore'
import SharedStyles from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/ViewControls.module.scss'
import { OptionSelectVariant } from 'common/OptionSelect/OptionSelect'
import { Button } from 'blaze-ui/Button'
import { Plus } from 'blaze-ui/icons'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { useCanViewAs } from 'components/Can'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Props {
  assignee?: {
    id: string
    type: AssigneeType
  }
  workspaceId: string
  assignedProperties: Array<AssignedProperty | VirtualAssignedProperty>
  className?: string
  hasMore?: boolean
  loading?: boolean
  onLoadMore?: () => void
  onCreateAssignedProperty: (property: Property) => Promise<void>
  onUpdateProperty: (property: Property) => void
  onUpdateAssignedProperty: (
    id: number | undefined,
    property: Property,
    assignedPropertyValue: AssignedPropertyValue[],
  ) => Promise<void>
  onRemoveAssignedProperty: (propertyId?: number) => Promise<void>
  onAddPropertyValueToAssignedProperties: (assignedPropertyId: number | undefined, propertyValue: PropertyValue) => void
  onRefreshAssignedProperty?: (property: Property) => void
  showEducationTooltip?: boolean
  setShowSection: (show: boolean) => void
  withoutBottomMargin?: boolean
  fitDropdownContent?: boolean
  variant?: OptionSelectVariant
  addPropertyButtonVariant?: ButtonVariant
}

// we're opening a property prompt for non existing property
// so we need a way to identify that case
const ADD_BUTTON_ID = 0

export const PropertiesList = ({
  assignee,
  assignedProperties,
  className,
  hasMore,
  loading,
  onLoadMore,
  onCreateAssignedProperty,
  onUpdateAssignedProperty,
  onUpdateProperty,
  onRemoveAssignedProperty,
  onAddPropertyValueToAssignedProperties,
  onRefreshAssignedProperty,
  workspaceId,
  showEducationTooltip,
  setShowSection,
  withoutBottomMargin = false,
  fitDropdownContent,
  variant,
  addPropertyButtonVariant = 'tertiary',
}: Props) => {
  const [query, setSearchQuery] = useState('')
  const {
    propertyPromptVisible,
    propertyValuesPromptVisible,
    createProperty,
    updateProperty,
    createAssignedProperty,
    createPropertyValue,
    updatePropertyValue,
    deletePropertyValue,
    createAssignedPropertyValue,
    openPropertyPrompt,
    closePropertyPrompt,
  } = usePropertiesList({
    assignee,
    workspaceId,
    onCreateAssignedProperty,
    onUpdateAssignedProperty,
    onUpdateProperty,
    onAddPropertyValueToAssignedProperties,
    onRefreshAssignedProperty,
  })
  // ensure properties are in redux store
  usePropertiesQuery({}, {}, { pushToStore: true })

  const currentWorkspaceId = useCurrentWorkspaceId()
  const canViewAs = useCanViewAs(currentWorkspaceId)
  const isMember = canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN])

  const highlightedPropNames = useSelector((state: RootState) => state.highlightedPropNames, isEqual)
  const { currentWorkspaceProperties, currentFolderProperties, filterByName, sortByName } = useProperties()
  const rootStore = useRootStore()
  const rightRail = rootStore.docStore?.rightRailStore

  const handleAddProperty = () => {
    if (!rightRail) return

    if (rightRail.locked) {
      openPropertyPrompt(ADD_BUTTON_ID)
    } else {
      rightRail.setLocked(true)
      rightRail.setAddingProperty(true)
    }
  }

  useEffect(() => {
    if (rightRail?.isAddingProperty && rightRail?.locked) {
      setTimeout(() => {
        openPropertyPrompt(ADD_BUTTON_ID)
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightRail?.isAddingProperty])

  const additionalAssignedProperties = [...currentWorkspaceProperties, ...currentFolderProperties]
    .map((property) => {
      if (!assignee) return null

      const alreadyAssigned = assignedProperties.some((assProp) => assProp.property.id === property.id)
      if (alreadyAssigned) return null

      const isCustomWorkspaceDefaultForArticles = property.default_for_articles && !property.default_type
      const isFolderProperty = !!property.scope_project_id

      if (isCustomWorkspaceDefaultForArticles || isFolderProperty) {
        return {
          id: undefined, // it is unassigned/virtual, so id is undefined
          property: property,
          property_values: [], // this attr means assigned values. there is none.
          assignee_id: assignee.id,
          assignee_type: assignee.type,
        } as AssignedProperty
      }

      return null
    })
    .filter((val) => val) as AssignedProperty[]

  const allAssignedProperties = [...assignedProperties, ...additionalAssignedProperties]

  useEffect(() => {
    if (!setShowSection) return

    setShowSection(!!allAssignedProperties.length)
  }, [allAssignedProperties.length, setShowSection])

  const unassignedProperties = currentWorkspaceProperties.filter(
    (property) => !allAssignedProperties.find((ap) => ap.property.id === property.id),
  )

  const availableProperties = sortByName(filterByName(unassignedProperties, query))

  useEffect(() => {
    if (propertyPromptVisible === ADD_BUTTON_ID) {
      PropertyService.getProperties(workspaceId, query)
    }
  }, [query, workspaceId, propertyPromptVisible])

  const article = useCurrentArticleMeta()
  const hasEditAccess = article?.current_user_access === Permission.Edit

  return (
    <div
      className={cx(className, Styles.container, { [Styles.withoutBottomMargin]: withoutBottomMargin })}
      id="properties-list-container"
    >
      {loading && !allAssignedProperties.length && <Skeleton title={false} paragraph={{ rows: 4, width: '100%' }} />}
      <ul className={Styles.list}>
        {allAssignedProperties.map(({ id, property, property_values }) => (
          <li className={Styles.listItem} key={property.id}>
            <FolderPropertyListItem
              variant={variant}
              assignee={assignee}
              property={property}
              isHighlighted={highlightedPropNames[property.name]}
              availableProperties={availableProperties}
              onSearch={setSearchQuery}
              selectedPropertyValues={property_values}
              disabled={!property.is_editable || !hasEditAccess}
              propertyActionsOpen={propertyPromptVisible === property.id && !propertyValuesPromptVisible}
              propertyValuesOpen={property.id === propertyValuesPromptVisible?.id}
              openPropertyActions={(propertyId: number | undefined) => openPropertyPrompt(propertyId)}
              onCreateProperty={createProperty}
              onUpdateProperty={updateProperty}
              onRemoveProperty={() => onRemoveAssignedProperty(property.id)}
              onSelectValue={(propertyValues: PropertyValue[] | DocumentPropertyValue[] | PersonPropertyValue[]) =>
                createAssignedPropertyValue(id, property, propertyValues)
              }
              onCreateValue={(propertyValue: PropertyValue) => createPropertyValue(id, propertyValue)}
              onUpdateValue={(propertyValue: PropertyValue) => updatePropertyValue(property, propertyValue)}
              onDeleteValue={(propertyValue: PropertyValue) => deletePropertyValue(property, propertyValue)}
            />
          </li>
        ))}
      </ul>
      {!loading && hasMore && (
        <button className={Styles.showMore} onClick={onLoadMore}>
          Show more...
        </button>
      )}
      {isMember && (
        <div className={Styles.addPropertyContainer}>
          {propertyPromptVisible === ADD_BUTTON_ID && !propertyValuesPromptVisible ? (
            <div className={cx(SharedStyles.control, Styles.propertiesControl)}>
              <div className={SharedStyles.content}>
                <PropertyActions
                  availableProperties={availableProperties}
                  onSearch={setSearchQuery}
                  onSelect={createAssignedProperty}
                  onCreate={createProperty}
                  onCancel={closePropertyPrompt}
                  calledFromDoc={assignee?.type === AssigneeType.Article}
                  openPropertyActions={(propertyId: number | undefined) => openPropertyPrompt(ADD_BUTTON_ID)}
                  fitDropdownContent={fitDropdownContent}
                />
              </div>
            </div>
          ) : (
            <Button
              frontIcon={Plus}
              size="md"
              aria-label="Add Property"
              variant={addPropertyButtonVariant}
              onPress={handleAddProperty}
            >
              Add Property
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
